import React, { useId, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FolderInput } from 'lucide-react';
import { useWikiV2Client } from '../../../hooks/useWikiV2';
import WikiIcon from '../shared/WikiIcon';
import { invalidateWiki, useWikiCategories } from '../shared/useWikiQueries';
import { wikiUi } from '../shared/wikiUi';
import WikiModalShell, { WikiCreateButton, WikiField, WikiModalError } from './WikiModalShell';
import { WikiLocationLayer, WikiLocationTrigger, useWikiLocationPicker } from './WikiLocationPicker';
import { WIKI_MANAGE_COPY, WIKI_MODAL_TOASTS } from './wikiModalCopy';
import { wikiModalErrorText } from './wikiModalErrors';
import { buildWikiLocations } from './wikiLocations';

const enc = encodeURIComponent;

/** The page's own location id: its subcategory, else its category. */
function pageLocationId(page) {
    return (page && (page.subcategory_id || page.category_id)) || null;
}

/**
 * Move destinations: the New page picker's list (live, writable locations —
 * `buildWikiLocations`, `writableOnly`) without the page's current location.
 * The server checks edit access on the destination too (SEC-6).
 */
export function moveDestinations(tree, page) {
    const here = pageLocationId(page);
    return buildWikiLocations(tree).filter((loc) => loc.id !== here);
}

/** `PATCH /wiki/pages/{id}` body for a destination row. */
export function movePayload(location) {
    return { category_id: location.categoryId, subcategory_id: location.subcategoryId || null };
}

/**
 * Move a page to another category or subcategory (#872). Opened with
 * `wikiUi.openModal('movePage', { page })` from the page's "⋯" menu. Reuses the
 * New page modal's location picker; only destinations the caller can edit are
 * offered. On success every Wiki query is refreshed (nav, counts, lists and the
 * page itself, whose crumbs change) and the page stays open.
 */
export default function WikiMovePageModal({ opts = {}, onClose }) {
    const page = opts.page || {};
    const client = useWikiV2Client();
    const queryClient = useQueryClient();
    const categories = useWikiCategories();
    const listboxId = useId();
    const [picked, setPicked] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const locations = useMemo(() => moveDestinations(categories.tree, page), [categories.tree, page]);
    const target = locations.find((loc) => loc.id === picked) || null;
    const picker = useWikiLocationPicker({ locations, currentId: picked, onPick: setPicked });

    const move = async () => {
        if (!target || busy || !page.page_id) return;
        setBusy(true);
        setError('');
        try {
            await client.patch(`/wiki/pages/${enc(page.page_id)}`, movePayload(target));
            onClose();
            wikiUi.toast(WIKI_MODAL_TOASTS.pageMoved(target.label));
            await invalidateWiki(queryClient);
        } catch (err) {
            setBusy(false);
            setError(wikiModalErrorText(err));
        }
    };

    return (
        <WikiModalShell
            title={WIKI_MANAGE_COPY.moveTitle(page.title || 'Untitled page')}
            onClose={onClose}
            testId="wiki-move-page-modal"
            beforeCard={<WikiLocationLayer picker={picker} listboxId={listboxId} />}
            footer={(
                <WikiCreateButton
                    enabled={Boolean(target)}
                    busy={busy}
                    onClick={move}
                    icon={FolderInput}
                    action="wiki.move_page"
                    testId="wiki-move-submit"
                >
                    {WIKI_MANAGE_COPY.moveSubmit}
                </WikiCreateButton>
            )}
        >
            <WikiField label={WIKI_MANAGE_COPY.moveLabel}>
                <WikiLocationTrigger
                    picker={picker}
                    label={target ? target.label : ''}
                    listboxId={listboxId}
                />
            </WikiField>
            {categories.isSuccess && locations.length === 0 && (
                <div className="wk-modal-note" data-testid="wiki-move-none">
                    <WikiIcon name="info" size={14} />
                    <span>{WIKI_MANAGE_COPY.moveNone}</span>
                </div>
            )}
            <div className="wk-modal-note" data-testid="wiki-move-note">
                <WikiIcon name="lock" size={14} />
                <span>{WIKI_MANAGE_COPY.moveNote}</span>
            </div>
            <WikiModalError message={error} />
        </WikiModalShell>
    );
}
